import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Chip,
    TextField,
    MenuItem,
    Grid,
    Divider,
    InputAdornment,
    Collapse,
    Button,
    IconButton,
    Badge,
    Tooltip,
    Select,
    Checkbox,
    ListItemText,
    OutlinedInput,
    FormControl,
    InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateIcon from '@mui/icons-material/Create';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminArticles, getAdminArticlesFilters } from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import ResponsiveDataView from '../../../components/admin/ResponsiveDataView';

// ── Paleta ────────────────────────────────────────────────────────────────────
const PRIMARY      = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const dateFieldSx = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const renderMultiValue = (selected, options) => {
    if (!selected.length) return '';
    if (selected.length === 1) return options?.find((o) => o.value === selected[0])?.text || selected[0];
    return `${selected.length} selecionadas`;
};

const CompactMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

const ArticlesCompactCard = ({ row, onClick }) => {
    const source = row.source || row.editorial_intelligence?.source || '—';

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 1.2,
                borderRadius: '8px',
                border: '1px solid #ececec',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
                        Fonte:
                    </Typography>
                    <Chip
                        label={source}
                        size="small"
                        sx={{
                            backgroundColor: PRIMARY,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 22,
                            maxWidth: 'calc(100% - 56px)',
                            '& .MuiChip-label': { px: 1.1, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: '0.78rem', color: '#222', lineHeight: 1.35 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
                        Titulo:
                    </Box>{' '}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            verticalAlign: 'top',
                        }}
                    >
                        {row.title || '—'}
                    </Box>
                </Typography>

                <CompactMetaField label="Data de criação" value={formatDate(row.created_at)} />
            </Box>
        </Paper>
    );
};

// ── Colunas da tabela ─────────────────────────────────────────────────────────
const COLUMNS = [
    {
        id: 'source',
        field: 'source',
        label: 'Fonte',
        minWidth: 140,
        render: (row) => (
            <Chip
                label={row.source}
                size="small"
                sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
            />
        ),
    },
    { id: 'title', field: 'title', label: 'Título', minWidth: 300 },
    {
        id: 'created_at',
        field: 'created_at',
        label: 'Data de criação',
        minWidth: 170,
        render: (row) => formatDate(row.created_at),
    },
];

// ── Componente principal ──────────────────────────────────────────────────────
export default function AdminArticlesPage() {
    const navigate  = useNavigate();
    const location  = useLocation();
    const isMobile  = useMediaQuery('(max-width:600px)');
    const isQuickFiltersCompact = useMediaQuery('(max-width:1024px)');
    const isCardsView = useMediaQuery('(max-width:1024px)');

    const navState = location.state || {};

    // dados
    const [items, setItems]     = useState([]);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(false);

    // opções do filtro "Fontes"
    const [filterOptions, setFilterOptions] = useState({ sources: [] });
    const [filtersLoading, setFiltersLoading] = useState(false);

    // filtros avançados
    const [filtersOpen, setFiltersOpen] = useState(navState.filtersOpen ?? !!(navState.createdAtStart || navState.createdAtEnd));
    const [createdAtStart, setCreatedAtStart] = useState(navState.createdAtStart || '');
    const [createdAtEnd, setCreatedAtEnd]     = useState(navState.createdAtEnd   || '');
    const [selectedSources, setSelectedSources] = useState(navState.sources || []);

    // filtros rápidos — apenas busca textual
    const [searchInput, setSearchInput] = useState(navState.search || '');
    const [search, setSearch]           = useState(navState.search || '');
    const [quickFiltersOpen, setQuickFiltersOpen] = useState(false);

    // ordenação e paginação
    const [orderBy,        setOrderBy]        = useState(navState.orderBy        || 'created_at');
    const [orderDirection, setOrderDirection] = useState(navState.orderDirection || 'desc');
    const [page,     setPage]     = useState(navState.page     ?? 0);
    const [pageSize, setPageSize] = useState(navState.pageSize || 10);

    const prevSearchInputRef = useRef(searchInput);

    const advancedFiltersCount = (createdAtStart ? 1 : 0) + (createdAtEnd ? 1 : 0) + (selectedSources.length ? 1 : 0);
    const totalActiveFilters   = advancedFiltersCount + (search ? 1 : 0);
    const quickFiltersCount = searchInput ? 1 : 0;

    useEffect(() => {
        loadFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const prev = prevSearchInputRef.current;
        prevSearchInputRef.current = searchInput;
        if (prev === searchInput) return;
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, search, selectedSources, createdAtStart, createdAtEnd, orderBy, orderDirection]);

    const loadFilters = async () => {
        setFiltersLoading(true);
        try {
            const data = await getAdminArticlesFilters();
            setFilterOptions({ sources: data.sources ?? [] });
        } catch {
            createNotification('error', 'Erro ao carregar filtros.', isMobile);
        } finally {
            setFiltersLoading(false);
        }
    };

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {
                order_by: orderBy,
                order_direction: orderDirection,
                page,
                page_size: pageSize,
            };
            if (search) params.search = search;
            if (selectedSources.length) params.sources = selectedSources;
            if (createdAtStart) params.created_at_start = `${createdAtStart}T00:00:00`;
            if (createdAtEnd)   params.created_at_end   = `${createdAtEnd}T23:59:59`;

            const data = await getAdminArticles(params);
            setItems(data.articles ?? []);
            setTotal(data.total ?? 0);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', 'Erro ao carregar matérias.', isMobile);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            setOrderDirection('asc');
        }
        setPage(0);
    };

    const handleClearAll = () => {
        setSearchInput('');
        setSearch('');
        setCreatedAtStart('');
        setCreatedAtEnd('');
        setSelectedSources([]);
        setPage(0);
    };

    const handleResetAdvanced = () => {
        setCreatedAtStart('');
        setCreatedAtEnd('');
        setSelectedSources([]);
        setPage(0);
    };

    const handleRefresh = () => setPage((p) => p);

    const handleSourcesChange = (e) => {
        const value = e.target.value;
        setSelectedSources(typeof value === 'string' ? value.split(',') : value);
        setPage(0);
    };

    const buildNavState = () => ({
        search, filtersOpen, createdAtStart, createdAtEnd, sources: selectedSources,
        orderBy, orderDirection, page, pageSize,
    });

    const handleRowClick = (row) => {
        if (row.editorial_intelligence_id) {
            navigate(`/admin/editorial-intelligence/${row.editorial_intelligence_id}/write-article`, { state: buildNavState() });
        } else {
            navigate(`/admin/articles/${row.id}/write`, { state: buildNavState() });
        }
    };

    return (
        <ResponsiveAdminPage>
            {/* ── Cabeçalho ─────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1, mb: 0.4 }}>
                    Matérias
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        Matérias escritas a partir de inteligência editorial ou de forma independente
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {totalActiveFilters > 0 && (
                            <Tooltip title="Limpar todos os filtros">
                                <Button
                                    size="small"
                                    onClick={handleClearAll}
                                    startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                        textTransform: 'none', color: '#888', fontSize: '0.78rem',
                                        '&:hover': { backgroundColor: '#f5f5f5', color: '#444' },
                                    }}
                                >
                                    Limpar
                                </Button>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', ml: 'auto' }}>
                            {isQuickFiltersCompact && (
                                <Badge
                                    badgeContent={quickFiltersCount}
                                    color="primary"
                                    sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                                >
                                    <Button
                                        size="small"
                                        startIcon={<FilterListIcon />}
                                        onClick={() => setQuickFiltersOpen((prev) => !prev)}
                                        variant={quickFiltersOpen ? 'contained' : 'outlined'}
                                        sx={quickFiltersOpen
                                            ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                            : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                        }
                                    >
                                        Filtros rápidos
                                    </Button>
                                </Badge>
                            )}
                            <Badge
                                badgeContent={advancedFiltersCount}
                                color="primary"
                                sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                            >
                                <Button
                                    size="small"
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setFiltersOpen((o) => !o)}
                                    variant={filtersOpen ? 'contained' : 'outlined'}
                                    sx={filtersOpen
                                        ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                        : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                    }
                                >
                                    Filtros avançados
                                </Button>
                            </Badge>
                            <Tooltip title="Atualizar dados">
                                <IconButton
                                    onClick={handleRefresh}
                                    size="small"
                                    disabled={loading}
                                    sx={{
                                        border: '1px solid #ddd', borderRadius: '6px',
                                        '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` },
                                    }}
                                >
                                    <RefreshIcon fontSize="small" sx={{
                                        animation: loading ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                                    }} />
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<CreateIcon fontSize="small" />}
                                onClick={() => navigate('/admin/articles/write')}
                                sx={{
                                    backgroundColor: PRIMARY,
                                    '&:hover': { backgroundColor: PRIMARY_DARK },
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Escrever matéria
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Filtros Avançados (colapsável) ────────────────────────── */}
            <Collapse in={filtersOpen}>
                <Paper sx={{
                    p: 2.5, mb: 2.5, borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 1.8, fontSize: '0.85rem' }}>
                        Filtros Avançados
                    </Typography>
                    <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                label="De"
                                type="date"
                                size="small"
                                fullWidth
                                value={createdAtStart}
                                onChange={(e) => { setCreatedAtStart(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                                sx={dateFieldSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                label="Até"
                                type="date"
                                size="small"
                                fullWidth
                                value={createdAtEnd}
                                onChange={(e) => { setCreatedAtEnd(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                                sx={dateFieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl size="small" fullWidth sx={dateFieldSx} disabled={filtersLoading}>
                                <InputLabel>Fontes</InputLabel>
                                <Select
                                    multiple
                                    value={selectedSources}
                                    onChange={handleSourcesChange}
                                    input={<OutlinedInput label="Fontes" />}
                                    renderValue={(selected) => renderMultiValue(selected, filterOptions.sources)}
                                >
                                    {filterOptions.sources.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>
                                            <Checkbox checked={selectedSources.includes(o.value)} size="small" />
                                            <ListItemText primary={o.text} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={handleResetAdvanced}
                                    sx={{ color: '#888', textTransform: 'none' }}
                                >
                                    Resetar
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setFiltersOpen(false)}
                                    sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }}
                                >
                                    Fechar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            <Collapse in={!isQuickFiltersCompact || quickFiltersOpen}>
                <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid #f0f0f0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 1.2, fontSize: '0.85rem' }}>
                        Filtros Rápidos
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Pesquise por título"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#bbb', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY },
                        }}
                    />
                </Paper>
            </Collapse>

            {/* ── Tabela ──────────────────────────────────────────────────── */}
            <Paper sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                p: isCardsView ? 1 : 0,
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <ResponsiveDataView
                        columns={COLUMNS}
                        rows={items}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        onSort={handleSort}
                        onRowClick={handleRowClick}
                        mobileBreakpoint={1024}
                        mobileCardRenderer={(row, index) => (
                            <ArticlesCompactCard
                                key={row.id ?? index}
                                row={row}
                                onClick={() => handleRowClick(row)}
                            />
                        )}
                        emptyMessage="Nenhuma matéria encontrada."
                        page={page}
                        pageSize={pageSize}
                        total={total}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                        paperSx={{ borderRadius: '8px', border: 'none', boxShadow: 'none' }}
                    />
                )}
            </Paper>
        </ResponsiveAdminPage>
    );
}
